/**
 * Sanitizes image URLs to handle malformed URLs from V1/V2 of the app
 * where URLs sometimes only contain the path or the full URL
 */

import { regEx } from '@/utils/global/regEx'
import { logger, EventCategory } from '@/utils/logger'
import { loggerSentry } from '@/utils/loggerSentry'

// Type definition for filled images array (array of image URLs/paths)
export type FilledImagesArray = string[] | null | undefined

/**
 * in V1/V2 of the app, urls have become malformed sometimes, leading to varying url states (i.e. just the path or the full url)
 * so this is a bandaid solution to correct the url until we can clean them all in the DB
 */
export const sanitizeCaseImageUrl = (
  filledImages: FilledImagesArray,
  caseImageIndex: number,
): string | null => {
  // Get the filled image path/URL from case data using the case image index
  const filledImagePathOrUrl = filledImages?.[caseImageIndex]

  if (!filledImagePathOrUrl) {
    return null
  }

  // Check if the filled image path is already a complete URL (starts with http)
  const filledImagePathIsAlreadyCompleteUrl =
    filledImagePathOrUrl.startsWith('http')

  // If already a complete URL, return it as-is
  if (filledImagePathIsAlreadyCompleteUrl) {
    return filledImagePathOrUrl
  }

  // Construct complete URL by prepending S3 bucket URL to the path
  // Normalize slashes to prevent double slashes or missing slashes in final URL
  const caseImagesBucketUrl = process.env.REACT_APP_S3BUCKET_CASE_IMAGES
  const normalizedBucketUrl =
    caseImagesBucketUrl?.replace(regEx.trailingSlashes, '') || ''
  const normalizedImagePath = filledImagePathOrUrl.replace(
    regEx.leadingSlashes,
    '',
  )
  const properCaseImageUrl = `${normalizedBucketUrl}/${normalizedImagePath}`

  // Log information about incomplete URL that was constructed
  const infoMessage = 'Incomplete case image URL detected and completed'
  const logData = {
    message: infoMessage,
    category: EventCategory.DISCREPANCY,
    data: {
      function: 'sanitizeCaseImageUrl',
      issue: 'incomplete_url_completed',
      caseImageIndex: caseImageIndex,
      originalImagePath: filledImagePathOrUrl,
      constructedImageUrl: properCaseImageUrl,
      caseImagesBucketUrl: caseImagesBucketUrl,
    },
  }
  logger.info(logData)
  loggerSentry.info(logData)

  return properCaseImageUrl
}
